import { Link } from "react-router-dom";
import { ArrowUpRight, Bed, Bath, Layers } from "lucide-react";
import { Container } from "../layout/Container.jsx";
import { imageOrPlaceholder, imgErrorFallback } from "../../lib/placeholders.js";
import { ImageWithSkeleton } from "../shared/ImageWithSkeleton.jsx";

/**
 * Homepage section showing up to 3 of the most recent home designs. Replaces
 * the old collections "RangeShowcase" now that designs are a flat catalogue.
 */
export function LatestDesigns({ designs = [] }) {
  const items = designs.slice(0, 3);
  if (items.length === 0) return null;

  return (
    <section className="border-t border-border-subtle py-20 lg:py-28">
      <Container>
        <div className="mb-12 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <p className="eyebrow">Home designs</p>
            <h2 className="mt-3 font-display text-4xl text-text-primary lg:text-5xl">
              Our latest designs
            </h2>
            <p className="mt-4 text-text-secondary">
              Fresh off the drawing board — every plan is designed in-house and built by our own
              teams.
            </p>
          </div>
          <Link
            to="/home-designs"
            className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent-hover"
          >
            View all designs <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {items.map((d, i) => {
            const key = d.slug ?? d._id ?? d.name ?? `design-${i}`;
            const heroImage = d.heroImage || d.images?.[0];
            return (
              <Link
                key={key}
                to={`/home-designs/${d.slug}`}
                className="group relative block aspect-[4/5] overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated"
              >
                <ImageWithSkeleton
                  src={imageOrPlaceholder(heroImage, { pool: "design", key })}
                  onError={imgErrorFallback(key)}
                  alt={d.name}
                  loading={i === 0 ? "eager" : "lazy"}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-base via-bg-base/30 to-transparent" />

                <div className="relative flex h-full flex-col justify-end p-6 lg:p-8">
                  <h3 className="font-display text-2xl text-text-primary lg:text-3xl">
                    {d.name}
                  </h3>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-text-secondary">
                    {d.bedrooms != null && (
                      <span className="inline-flex items-center gap-1">
                        <Bed className="h-3.5 w-3.5 text-accent" />
                        {d.bedrooms} bed
                      </span>
                    )}
                    {d.bathrooms != null && (
                      <span className="inline-flex items-center gap-1">
                        <Bath className="h-3.5 w-3.5 text-accent" />
                        {d.bathrooms} bath
                      </span>
                    )}
                    {d.storey && (
                      <span className="inline-flex items-center gap-1">
                        <Layers className="h-3.5 w-3.5 text-accent" />
                        {d.storey} storey
                      </span>
                    )}
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm text-accent transition-transform group-hover:translate-x-1">
                    View design
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </Container>
    </section>
  );
}
